import type { QueryClient } from '@tanstack/react-query';
import { useAuthStore, type AuthUser } from '@/store/authStore';
import { FEATURES_KEY, isOn, parseFeatures } from '@/lib/features';

/**
 * SESSION HYGIENE (switch SESSION_REFRESH, "Refresh sign-in").
 *
 * Two gaps, both on a shared office machine:
 *
 *  - Cached data outlived its owner. Signing out navigated to the sign-in page and left
 *    TanStack Query's cache as it was (5-minute stale time, no refetch on focus), so the
 *    next person to sign in on the same tab was shown the previous person's candidates,
 *    reports and notifications until each query happened to refetch.
 *  - The role was never read again. It came from the sign-in response and stayed in the
 *    persisted store, so someone demoted kept a menu and pages the server now refused them.
 *    (The re-reading is the session keeper's: components/layout/SessionKeeper.tsx.)
 *
 * With the switch on, the cache is emptied whenever the signed-in account changes: on
 * sign-out (this tab's, another tab's broadcast, or an expired session), when a different
 * person signs in, and when the same person's role changes — a demotion must not leave
 * data on screen that the new role may not see. A renamed account keeps its cache.
 *
 * One exception does not wait for the switch: an account change to or from an INTERVIEWER
 * always empties the cache. The interviewer lock is always on (it affects only the new role,
 * plan: behaviourChanges), and an Interviewer must never inherit a staff member's cached
 * candidates in the browser any more than from the server.
 *
 * The switch is read from the same QueryClient's cached /features answer, at the moment of
 * the change: there is no module state to leak between clients or tests.
 */

/** Whether going from `prev` to `next` must empty the cache. Pure, for the tests. */
export function shouldClearCache(prev: AuthUser | null, next: AuthUser | null, sessionRefreshOn: boolean): boolean {
  const changed = (prev?.id ?? null) !== (next?.id ?? null) || (!!prev && !!next && prev.role !== next.role);
  if (!changed) return false;
  if (prev?.role === 'INTERVIEWER' || next?.role === 'INTERVIEWER') return true;
  return sessionRefreshOn;
}

/**
 * Empty `queryClient` whenever the signed-in account changes as above. Returns the
 * unsubscribe. Installed once, at the top of the app (App.tsx), so it is in place on every
 * page — the sign-in page included, where the next person signs in.
 */
export function installSessionHygiene(queryClient: QueryClient): () => void {
  return useAuthStore.subscribe((state, prev) => {
    if (state.user === prev.user) return;
    const sessionRefreshOn = isOn(parseFeatures(queryClient.getQueryData(FEATURES_KEY)), 'SESSION_REFRESH');
    if (!shouldClearCache(prev.user, state.user, sessionRefreshOn)) return;
    // Stop what is in flight first, so a response for the last account cannot land in the
    // emptied cache after it.
    void queryClient.cancelQueries();
    queryClient.clear();
  });
}

// "After any refusal": the signal lives in lib/refusals.ts, which the API client imports.
export { onRefusal, signalRefusal } from '@/lib/refusals';

// ── Reconciling with /auth/me ───────────────────────────────────────────────────

/** What GET /api/auth/me answers (auth.service getMe), as far as the app uses it. */
export interface ServerUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: AuthUser['role'];
  organization?: AuthUser['organization'];
}

/**
 * Bring the stored account in line with what the server says it is.
 *  - Another account behind the same session (a sign-in on another tab shares the cookie):
 *    the stored one is replaced, keeping the access token — which the hygiene treats as an
 *    account change.
 *  - The same account with another role, name or email: those fields are updated; a role
 *    change empties the cache (above), and the role gate re-routes at once.
 *  - Nothing changed: nothing is written, so nothing re-renders.
 */
export function reconcileSignedInUser(server: ServerUser | null | undefined): void {
  const { user, accessToken, setAuth, updateUser } = useAuthStore.getState();
  if (!user || !server?.id || !server.role) return;
  if (server.id !== user.id) {
    setAuth(
      { id: server.id, email: server.email, firstName: server.firstName, lastName: server.lastName, role: server.role, organization: server.organization ?? null },
      accessToken ?? '',
    );
    return;
  }
  const changes: Partial<AuthUser> = {};
  if (server.role !== user.role) changes.role = server.role;
  if (server.firstName !== user.firstName) changes.firstName = server.firstName;
  if (server.lastName !== user.lastName) changes.lastName = server.lastName;
  if (server.email !== user.email) changes.email = server.email;
  if (Object.keys(changes).length) updateUser(changes);
}
